/**
 * Pure geometry helpers for the Floating Action Burst.
 *
 * This module contains no React, no side effects and no DOM lookups.
 * It exposes the math used by `FloatingActionBurst` to decide:
 *   - where the burst items fit on an arc around the main FAB,
 *   - whether the arc should fall back to a line layout,
 *   - how line items are distributed across the viewport.
 *
 * Keeping the geometry isolated makes it trivial to reason about and
 * to unit-test without mounting the component. Tunable thresholds and
 * margins live in `floatingActionBurst.configuration.ts`.
 */

import {
    ARC_ENDPOINT_INSET_DEG,
    ARC_ENDPOINT_INSET_THRESHOLD_DEG,
    ARC_MAX_RADIUS_PX,
    ARC_MIN_RADIUS_PX,
    ARC_RADIUS_GROW_STEP_PX,
    ARC_RADIUS_SHRINK_STEP_PX,
    MAX_ARC_LENGTH_DEG,
    MIN_ARC_LENGTH_DEG,
    MIN_ITEM_ANGULAR_DENSITY_DEG,
    MIN_LINE_SPACING_PX,
    SUB_FAB_HALF_BOX_PX,
    VIEWPORT_EDGE_MARGIN,
} from "./floatingActionBurst.configuration";

/** Supported expansion modes for the burst. */
export type FabExpansionMode = "arc" | "line";

/** Side of the viewport closest to the main FAB. */
export type CardinalSide = "left" | "right" | "up" | "down";

/** Cartesian offset of a child item relative to the main FAB center. */
export interface ItemOffset {
    "dx": number;
    "dy": number;
}

/** Screen-space clearances around the main FAB used by both modes. */
export interface DirectionClearances {
    "left":  number;
    "right": number;
    "up":    number;
    "down":  number;
}

/** Layout decision computed for a given render. */
export interface ComputedLayout {

    /**
     * Effective mode used for THIS render, which may differ from the
     * user-preferred mode when a fallback to "line" was required.
     */
    "effectiveMode": FabExpansionMode;

    /** Cartesian offsets for each child, indexed by child position. */
    "offsets": readonly ItemOffset[];

    /** Arc radius (px) when `effectiveMode === "arc"`. */
    "arcRadiusPx": number;

    /** Arc start angle (deg, screen convention: 0 = right, 90 = down). */
    "arcStartDeg": number;

    /** Arc sweep length (deg) when `effectiveMode === "arc"`. */
    "arcLengthDeg": number;
}

/** Number of angular slots (one per integer degree) in a clearance mask. */
const ANGULAR_MASK_SLOT_COUNT = 360;

/**
 * Convert degrees to radians for local trigonometry.
 *
 * @param degrees Angle in degrees.
 * @returns Equivalent angle in radians.
 */
export const degToRad = ( degrees: number ): number => {
    return ( degrees * Math.PI ) / 180;
};

/**
 * Detect the cardinal side of the viewport closest to the main FAB.
 * Used to orient a near-full arc so it "opens" toward the closest edge.
 *
 * @param clearances Per-direction viewport clearances (px).
 * @returns The cardinal side with the smallest clearance.
 */
export const pickClosestEdge = ( clearances: DirectionClearances ): CardinalSide => {
    const pairs: readonly [ CardinalSide, number ][] = [
        [ "left", clearances.left ],
        [ "right", clearances.right ],
        [ "up", clearances.up ],
        [ "down", clearances.down ],
    ];
    let closestSide: CardinalSide = "right";
    let closestValue = Number.POSITIVE_INFINITY;
    for ( const [ side, value ] of pairs ) {
        if ( value < closestValue ) {
            closestValue = value;
            closestSide = side;
        }
    }

    return closestSide;
};

/**
 * Convert a cardinal side to the corresponding screen-convention angle.
 *
 * @param side Cardinal side of the viewport.
 * @returns Angle in degrees (0 = right, 90 = down, 180 = left, 270 = up).
 */
export const cardinalToAngleDeg = ( side: CardinalSide ): number => {
    if ( side === "right" ) {
        return 0;
    }
    if ( side === "down" ) {
        return 90;
    }
    if ( side === "left" ) {
        return 180;
    }

    return 270;
};

/**
 * Build a boolean clearance mask where `mask[d] === true` means an item
 * placed at angle `d` on a circle of `radius` still fits the viewport.
 *
 * @param fabCx X coordinate of the main FAB center (viewport px).
 * @param fabCy Y coordinate of the main FAB center (viewport px).
 * @param viewportWidth Current viewport width (px).
 * @param viewportHeight Current viewport height (px).
 * @param radius Candidate arc radius (px).
 * @param safeMarginTopPx Top safe area (px).
 * @param safeMarginBottomPx Bottom safe area (px).
 * @returns A 360-slot boolean array (one entry per integer degree).
 */
export const buildAngularClearanceMask = (
    fabCx: number,
    fabCy: number,
    viewportWidth: number,
    viewportHeight: number,
    radius: number,
    safeMarginTopPx: number,
    safeMarginBottomPx: number,
): boolean[] => {
    const mask: boolean[] = new Array<boolean>( ANGULAR_MASK_SLOT_COUNT ).fill( false );
    for ( let degree = 0; degree < ANGULAR_MASK_SLOT_COUNT; degree++ ) {
        const rad = degToRad( degree );
        const itemCx = fabCx + radius * Math.cos( rad );
        const itemCy = fabCy + radius * Math.sin( rad );
        const fits =
            itemCx - SUB_FAB_HALF_BOX_PX >= VIEWPORT_EDGE_MARGIN &&
            itemCx + SUB_FAB_HALF_BOX_PX <= viewportWidth - VIEWPORT_EDGE_MARGIN &&
            itemCy - SUB_FAB_HALF_BOX_PX >= safeMarginTopPx &&
            itemCy + SUB_FAB_HALF_BOX_PX <= viewportHeight - safeMarginBottomPx;
        mask[degree] = fits;
    }

    return mask;
};

/**
 * Find the longest contiguous run of `true` values in an angular mask,
 * accounting for the 359 -> 0 wrap-around seam.
 *
 * @param mask 360-slot boolean clearance mask.
 * @returns The detected arc as `{ startDeg, lengthDeg }`. `lengthDeg === 0`
 *          when the mask contains only `false` values.
 */
export const findLongestContiguousArc = ( mask: readonly boolean[] ): {
    "startDeg":  number;
    "lengthDeg": number;
} => {
    const total = mask.length;

    // Count truthy slots to short-circuit the full-coverage / empty cases.
    let trueCount = 0;
    for ( const entry of mask ) {
        if ( entry ) {
            trueCount++;
        }
    }
    if ( trueCount === total ) {
        return {
            "startDeg":  0,
            "lengthDeg": total,
        };
    }
    if ( trueCount === 0 ) {
        return {
            "startDeg":  0,
            "lengthDeg": 0,
        };
    }

    /*
     * Walk the mask twice (virtual double-array) to handle wrap-around.
     * Track the current run length and its start, resetting on false.
     */
    let bestStart = 0;
    let bestLength = 0;
    let currentStart = 0;
    let currentLength = 0;
    for ( let index = 0; index < total * 2; index++ ) {
        const isTrue = mask[index % total];
        if ( isTrue ) {
            if ( currentLength === 0 ) {
                currentStart = index % total;
            }
            currentLength++;
            if ( currentLength > bestLength ) {
                bestLength = currentLength;
                bestStart = currentStart;
            }
        } else {
            currentLength = 0;
        }
    }

    // Cap at `total` for the "wrap-around meeting itself" degenerate case.
    if ( bestLength > total ) {
        bestLength = total;
    }

    return {
        "startDeg":  bestStart,
        "lengthDeg": bestLength,
    };
};

/**
 * Orient a near-full arc (> `MAX_ARC_LENGTH_DEG`) so its opening faces
 * the closest edge, preventing items from overlapping the main FAB area.
 *
 * @param clearances Viewport clearances for the four cardinal directions.
 * @returns A `{ startDeg, lengthDeg }` describing the oriented arc.
 */
export const orientLargeArc = ( clearances: DirectionClearances ): {
    "startDeg":  number;
    "lengthDeg": number;
} => {
    const closestEdge = pickClosestEdge( clearances );

    /*
     * The "opening" faces the closest edge; the arc therefore centers on
     * the opposite direction and spans `MAX_ARC_LENGTH_DEG`.
     */
    const openingAngleDeg = cardinalToAngleDeg( closestEdge );
    const arcCenterDeg = ( openingAngleDeg + 180 ) % 360;
    const arcLengthDeg = MAX_ARC_LENGTH_DEG;
    const startDeg = ( arcCenterDeg - arcLengthDeg / 2 + 360 ) % 360;

    return {
        "startDeg":  startDeg,
        "lengthDeg": arcLengthDeg,
    };
};

/**
 * Distribute item offsets evenly on an arc segment. Applies a small
 * inset to the endpoints for wide arcs to avoid clipping visuals.
 *
 * @param startDeg Arc start angle (deg).
 * @param lengthDeg Arc sweep length (deg).
 * @param radius Arc radius (px).
 * @param childrenCount Number of child items to lay out.
 * @returns Offsets relative to the main FAB center, indexed by child.
 */
export const distributeOffsetsOnArc = (
    startDeg: number,
    lengthDeg: number,
    radius: number,
    childrenCount: number,
): ItemOffset[] => {
    if ( childrenCount <= 0 ) {
        return [];
    }

    const insetDeg = lengthDeg > ARC_ENDPOINT_INSET_THRESHOLD_DEG ? ARC_ENDPOINT_INSET_DEG : 0;
    const usableStart = startDeg + insetDeg;
    const usableLength = lengthDeg - 2 * insetDeg;

    const offsets: ItemOffset[] = [];
    for ( let index = 0; index < childrenCount; index++ ) {
        const t = childrenCount === 1 ? 0.5 : index / ( childrenCount - 1 );
        const angleDeg = usableStart + t * usableLength;
        const rad = degToRad( angleDeg );
        offsets.push( {
            "dx": radius * Math.cos( rad ),
            "dy": radius * Math.sin( rad ),
        } );
    }

    return offsets;
};

/**
 * Baseline radius (px) for an arc layout as a function of the child count.
 * Grows slightly with the number of items to keep angular density low.
 *
 * @param childrenCount Number of children rendered around the main FAB.
 * @returns A radius (px) clamped into a comfortable visual range.
 */
export const getBaselineArcRadiusPx = ( childrenCount: number ): number => {
    const candidate = 72 + childrenCount * 8;
    if ( candidate < 90 ) {
        return 90;
    }
    if ( candidate > 160 ) {
        return 160;
    }

    return candidate;
};

/**
 * Compute the adaptive arc layout using progressive radius tuning.
 *
 * The algorithm:
 *   1. Try the baseline radius derived from the child count.
 *   2. If items are too crowded, grow the radius up to the max.
 *   3. If the arc is too short, shrink the radius down to the min.
 *   4. If nothing satisfies the density requirement, return the best
 *      candidate seen so far so the caller can still fall back to line.
 *
 * @param fabCx X coordinate of the main FAB center (viewport px).
 * @param fabCy Y coordinate of the main FAB center (viewport px).
 * @param viewportWidth Current viewport width (px).
 * @param viewportHeight Current viewport height (px).
 * @param safeMarginTopPx Top safe area (px).
 * @param safeMarginBottomPx Bottom safe area (px).
 * @param childrenCount Number of child items.
 * @param clearances Per-direction clearances used for 300+ arc orientation.
 * @returns A usable layout, or `null` when no arc segment fits at all.
 */
export const computeArcLayout = (
    fabCx: number,
    fabCy: number,
    viewportWidth: number,
    viewportHeight: number,
    safeMarginTopPx: number,
    safeMarginBottomPx: number,
    childrenCount: number,
    clearances: DirectionClearances,
): ComputedLayout | null => {
    const baselineRadius = getBaselineArcRadiusPx( childrenCount );

    /*
     * Candidate radii tried in order of visual preference: grow first
     * for density, then shrink if the available arc turns out too short.
     */
    const candidateRadii: number[] = [];
    candidateRadii.push( baselineRadius );
    for ( let radius = baselineRadius + ARC_RADIUS_GROW_STEP_PX; radius <= ARC_MAX_RADIUS_PX; radius += ARC_RADIUS_GROW_STEP_PX ) {
        candidateRadii.push( radius );
    }
    for ( let radius = baselineRadius - ARC_RADIUS_SHRINK_STEP_PX; radius >= ARC_MIN_RADIUS_PX; radius -= ARC_RADIUS_SHRINK_STEP_PX ) {
        candidateRadii.push( radius );
    }

    let fallback: ComputedLayout | null = null;

    for ( const radius of candidateRadii ) {
        const mask = buildAngularClearanceMask(
            fabCx,
            fabCy,
            viewportWidth,
            viewportHeight,
            radius,
            safeMarginTopPx,
            safeMarginBottomPx,
        );
        const detected = findLongestContiguousArc( mask );
        if ( detected.lengthDeg < MIN_ARC_LENGTH_DEG ) {
            continue;
        }

        let startDeg = detected.startDeg;
        let lengthDeg = detected.lengthDeg;
        if ( lengthDeg > MAX_ARC_LENGTH_DEG ) {
            const oriented = orientLargeArc( clearances );
            startDeg = oriented.startDeg;
            lengthDeg = oriented.lengthDeg;
        }

        const density = childrenCount > 0 ? lengthDeg / childrenCount : 0;
        const layout: ComputedLayout = {
            "effectiveMode": "arc",
            "offsets":       distributeOffsetsOnArc( startDeg, lengthDeg, radius, childrenCount ),
            "arcRadiusPx":   radius,
            "arcStartDeg":   startDeg,
            "arcLengthDeg":  lengthDeg,
        };

        if ( density >= MIN_ITEM_ANGULAR_DENSITY_DEG ) {
            return layout;
        }

        /*
         * Remember the widest candidate as a fallback. It may still beat
         * a line layout when the viewport is heavily constrained.
         */
        if ( fallback === null || layout.arcLengthDeg > fallback.arcLengthDeg ) {
            fallback = layout;
        }
    }

    return fallback;
};

/**
 * Compute the line layout by choosing the direction with the largest
 * clearance among the four cardinals. Ties follow the deterministic
 * order `left > down > right > up`.
 *
 * @param clearances Per-direction clearances (px).
 * @param childrenCount Number of child items.
 * @param preferredSpacingPx Desired pixel spacing requested by the caller.
 * @param forcedLineSide When set, skips auto direction selection and uses this cardinal side.
 * @returns A line layout descriptor with the chosen direction's offsets.
 */
export const computeLineLayout = (
    clearances: DirectionClearances,
    childrenCount: number,
    preferredSpacingPx: number,
    forcedLineSide?: CardinalSide,
): ComputedLayout => {
    if ( childrenCount <= 0 ) {
        return {
            "effectiveMode": "line",
            "offsets":       [],
            "arcRadiusPx":   0,
            "arcStartDeg":   0,
            "arcLengthDeg":  0,
        };
    }

    let chosenSide: CardinalSide;
    let chosenClearance: number;

    if ( forcedLineSide !== undefined ) {
        chosenSide = forcedLineSide;
        chosenClearance = clearances[chosenSide];
    } else {
        // Preference order doubles as the tie-breaker for equal clearances.
        const orderedSides: readonly CardinalSide[] = [ "left", "down", "right", "up" ];
        const requiredSpace = ( childrenCount * preferredSpacingPx ) + VIEWPORT_EDGE_MARGIN;

        chosenSide = "left";
        chosenClearance = -1;
        for ( const side of orderedSides ) {
            const clearance = clearances[side];
            if ( clearance >= requiredSpace && clearance > chosenClearance ) {
                chosenClearance = clearance;
                chosenSide = side;
            }
        }

        // If no direction satisfies the required space, pick the largest one.
        if ( chosenClearance < 0 ) {
            for ( const side of orderedSides ) {
                if ( clearances[side] > chosenClearance ) {
                    chosenClearance = clearances[side];
                    chosenSide = side;
                }
            }
        }
    }

    // Adjust spacing when the chosen side cannot hold the preferred gap.
    const maxSpacingForChosenSide = Math.max(
        MIN_LINE_SPACING_PX,
        Math.floor( ( chosenClearance - VIEWPORT_EDGE_MARGIN ) / Math.max( childrenCount, 1 ) ),
    );
    const spacingPx = Math.min( preferredSpacingPx, maxSpacingForChosenSide );

    const offsets: ItemOffset[] = [];
    for ( let index = 0; index < childrenCount; index++ ) {
        const distance = ( index + 1 ) * spacingPx;
        if ( chosenSide === "left" ) {
            offsets.push( {
                "dx": -distance,
                "dy": 0,
            } );
        } else if ( chosenSide === "right" ) {
            offsets.push( {
                "dx": distance,
                "dy": 0,
            } );
        } else if ( chosenSide === "up" ) {
            offsets.push( {
                "dx": 0,
                "dy": -distance,
            } );
        } else {
            offsets.push( {
                "dx": 0,
                "dy": distance,
            } );
        }
    }

    return {
        "effectiveMode": "line",
        "offsets":       offsets,
        "arcRadiusPx":   0,
        "arcStartDeg":   0,
        "arcLengthDeg":  0,
    };
};

/**
 * Build the SVG `d` attribute for the arc trace between two angles on a
 * circle centered at the SVG origin (the svg viewBox is centered on the
 * main FAB).
 *
 * @param radius Arc radius (px).
 * @param startDeg Arc start angle (deg, screen convention).
 * @param lengthDeg Arc sweep length (deg).
 * @returns The SVG path data describing the arc.
 */
export const buildArcSvgPath = ( radius: number, startDeg: number, lengthDeg: number ): string => {
    const startRad = degToRad( startDeg );
    const endRad = degToRad( startDeg + lengthDeg );
    const startX = radius * Math.cos( startRad );
    const startY = radius * Math.sin( startRad );
    const endX = radius * Math.cos( endRad );
    const endY = radius * Math.sin( endRad );
    const largeArc = lengthDeg > 180 ? 1 : 0;
    const sweep = 1;

    return `M ${ startX } ${ startY } A ${ radius } ${ radius } 0 ${ largeArc } ${ sweep } ${ endX } ${ endY }`;
};
